




import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useEffect } from "react";

import { Stack } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Alert, Button, TextField } from "@mui/material";

import { useTranslate } from "src/locales";
import { generateResendErrorMessage, generateVerificationErrorMessage, generateRequestPhoneVerificationErrorMessage } from "src/services/account/account.utils";
import { useGetMyAccountProfileQuery, useVerifyPhoneNumberMutation, useUpdateMyAccountProfileMutation, useResendPhoneVerificationMutation, useRequestPhoneVerificationMutation } from "src/services/account/account.service";

export interface PhoneSetupModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export default function PhoneSetupModal({
    open,
    onClose,
    onSuccess
}: PhoneSetupModalProps) {

    const { t } = useTranslate();

    const { currentData: accountProfile } = useGetMyAccountProfileQuery();

    const [step, setStep] = useState<'request' | 'verification'>('request');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [countryCode, setCountryCode] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [verificationCode, setVerificationCode] = useState<string>('');

    const [updateProfile, { isLoading: updatingProfile }] = useUpdateMyAccountProfileMutation();
    const [requestVerification, { isLoading: requestVerificationLoading }] = useRequestPhoneVerificationMutation();
    const [verifyPhone, { isLoading: verifyPhoneLoading }] = useVerifyPhoneNumberMutation();
    const [resendCode, { isLoading: resendCodeLoading }] = useResendPhoneVerificationMutation();

    const verificationRequired = useMemo(() => {
        if (!accountProfile) return false;
        if (!accountProfile.phone_verified) {
            return true;
        }
        return false;
    }, [accountProfile]);

    const handleClose = () => {
        setPhoneNumber('');
        setCountryCode('');
        setErrorMessage('');
        setVerificationCode('');
        setStep('request');
        onClose();
    }

    const handleSuccess = () => {
        setPhoneNumber('');
        setCountryCode('');
        setErrorMessage('');
        setVerificationCode('');
        setStep('request');
        onSuccess();
    }

    useEffect(() => {
        if (!accountProfile) return;

        // Show the full international number (e.g. +61400123456) instead of the bare national
        // number. The backend re-splits it into country_code + phone on save (normalizePhone),
        // so this round-trips safely while country_code stays in state.
        const nationalNumber = accountProfile.phone ?? '';
        const dialCode = accountProfile.country_code ?? '';
        setPhoneNumber(nationalNumber && dialCode && !nationalNumber.startsWith('+') ? `${dialCode}${nationalNumber}` : nationalNumber);
        setCountryCode(accountProfile.country_code ?? '');

    }, [accountProfile]);

    const handleRequestVerification = async () => {

        setErrorMessage('');

        if (!phoneNumber) {
            setErrorMessage(t('account.phone_setup.modal.validation.empty'));
            return;
        }

        try {
            await updateProfile({
                phone: phoneNumber,
                country_code: countryCode
            }).unwrap();

            await requestVerification().unwrap();

            setStep('verification');
            enqueueSnackbar(t('account.phone_setup.modal.api.request_verification.success'), { variant: 'success' });
        }
        catch (error) {
            setErrorMessage(generateRequestPhoneVerificationErrorMessage(error));
        }
    }

    const handleVerification = async () => {

        setErrorMessage('');

        try {
            await verifyPhone({
                code: verificationCode,
            }).unwrap();

            enqueueSnackbar(t('account.phone_setup.modal.api.verify.success'), { variant: 'success' });
            handleSuccess();
        }
        catch (error) {
            setErrorMessage(generateVerificationErrorMessage(error));
        }
    }

    const handleResendCode = async () => {
        setErrorMessage('');

        try {
            await resendCode().unwrap();

            enqueueSnackbar(t('account.phone_setup.modal.api.resend.success'), { variant: 'success' });
        }
        catch (error) {
            setErrorMessage(generateResendErrorMessage(error));
        }
    }

    return (
        <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
            <DialogTitle sx={{ pb: 2 }}>
                {t('account.phone_setup.modal.title')}
            </DialogTitle>

            <DialogContent sx={{ typography: 'body2' }}>

                {
                    verificationRequired && (

                        <Stack direction="column" spacing={1} alignItems='start' justifyContent="center">

                            {
                                step === 'request' && (
                                    <>
                                        <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
                                            {t('account.phone_setup.modal.request_alert')}
                                        </Alert>

                                        <Stack direction='row' spacing={1} alignItems='center' justifyContent="center" width="100%">
                                            <TextField
                                                fullWidth
                                                label={t('account.phone_setup.modal.labels.phone_number')}
                                                placeholder="+61400123456"
                                                value={phoneNumber}
                                                onChange={(e) => setPhoneNumber(e.target.value)}
                                                required={false}
                                                error={!!errorMessage}
                                            />
                                        </Stack>
                                    </>
                                )
                            }

                            {
                                step === 'verification' && (
                                    <>
                                        <Alert severity="info" sx={{ mb: 2, width: "100%" }}>
                                            {t('account.phone_setup.modal.verification_alert')}
                                        </Alert>

                                        <Stack direction='row' spacing={1} alignItems='center' justifyContent="center" width="100%">
                                            <TextField
                                                fullWidth
                                                label={t('account.phone_setup.modal.labels.verification_code')}
                                                value={verificationCode}
                                                onChange={(e) => setVerificationCode(e.target.value)}
                                                required={false}
                                                error={!!errorMessage}
                                            />
                                        </Stack>
                                    </>
                                )
                            }


                            {
                                errorMessage && (
                                    <Alert severity="error" sx={{ width: "100%" }}>
                                        {errorMessage}
                                    </Alert>
                                )
                            }
                        </Stack>
                    )
                }

                {
                    !verificationRequired && (
                        <Alert severity="success" sx={{ mb: 2, width: "100%" }}>
                                {t('account.phone_setup.modal.not_required_alert')}
                            </Alert>
                    )
                }


            </DialogContent>

            <DialogActions>

                {
                    verificationRequired && (
                        <>
                            {
                                step === 'request' && (
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        onClick={handleRequestVerification}
                                        loading={requestVerificationLoading || updatingProfile}
                                        disabled={requestVerificationLoading || updatingProfile}
                                    >
                                        {t('account.phone_setup.modal.actions.request_verification')}
                                    </LoadingButton>
                                )
                            }

                            {
                                step === 'verification' && (
                                    <>

                                        <LoadingButton variant="contained" color="primary" onClick={handleVerification}>
                                            {t('account.phone_setup.modal.actions.verify')}
                                        </LoadingButton>
                                        <LoadingButton variant="contained" onClick={handleResendCode}>
                                            {t('account.phone_setup.modal.actions.resend')}
                                        </LoadingButton>
                                    </>
                                )
                            }

                        </>
                    )
                }

                <Button variant="outlined" color="inherit" onClick={handleClose}>
                    {t('common.cancel')}
                </Button>
            </DialogActions>
        </Dialog >
    )
}